import React from 'react';
import { Box } from '@chakra-ui/react';
import { CaretDown, CaretUp, Check, X } from 'phosphor-react';
import { components as selectComponents, StylesConfig } from 'react-select';

interface SelectStyleOptions {
  size?: 'sm' | 'md';
  hasError?: boolean;
  maxWidth?: string;
}

const colors = {
  strong: '#292929',
  body: '#5D5D5D',
  faint: '#9E9E9E',
  hairline: '#F2F2F2',
  input: '#EBEBEB',
  inputHover: '#DCDCDC',
  ring: '#C9C9C9',
  selected: '#F5F5F5',
  base: '#FFFFFF',
  destructive: '#D63D3D',
  destructiveSoft: 'rgba(214, 61, 61, 0.36)',
  destructiveStrong: 'rgba(214, 61, 61, 0.64)',
  destructiveRing: '0 0 0 3px rgba(214, 61, 61, 0.16)',
};

export const buildSelectStyles = ({
  size = 'md',
  hasError = false,
  maxWidth,
}: SelectStyleOptions = {}): StylesConfig<any, any> => {
  const height = size === 'sm' ? 34 : 38;
  const paddingX = size === 'sm' ? 9 : 11;

  return {
    container: (provided) => ({ ...provided, width: '100%', maxWidth }),
    menuPortal: (provided) => ({ ...provided, zIndex: 1500 }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      width: '100%',
      maxWidth,
      marginTop: '4px',
      marginBottom: '4px',
      padding: '4px',
      borderRadius: '10px',
      border: `1px solid ${colors.hairline}`,
      boxShadow: '0 0 0 1px rgba(41, 41, 41, 0.06), 0 8px 24px rgba(41, 41, 41, 0.10)',
      backgroundColor: colors.base,
      overflow: 'hidden',
      fontSize: '13px',
    }),
    menuList: (provided) => ({ ...provided, padding: 0 }),
    control: (styles, { isDisabled, isFocused }) => {
      let borderColor = colors.input;
      if (hasError && isFocused) borderColor = colors.destructiveStrong;
      else if (hasError) borderColor = colors.destructiveSoft;
      else if (isFocused) borderColor = colors.ring;

      let boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
      if (isFocused)
        boxShadow = hasError ? colors.destructiveRing : '0 0 0 3px rgba(201, 201, 201, 0.24)';
      if (isDisabled) boxShadow = 'none';

      return {
        ...styles,
        minHeight: `${height}px`,
        backgroundColor: isDisabled ? colors.selected : colors.base,
        borderRadius: '10px',
        borderColor,
        boxShadow,
        fontSize: '13px',
        lineHeight: '20px',
        cursor: 'pointer',
        opacity: isDisabled ? 0.64 : 1,
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': { borderColor: hasError ? colors.destructiveSoft : colors.inputHover },
      };
    },
    valueContainer: (provided, state) => ({
      ...provided,
      padding: state.isMulti && state.hasValue ? `3px ${paddingX}px` : `0 ${paddingX}px`,
      gap: '4px',
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      color: colors.strong,
      '& input': { boxShadow: 'none !important', outline: 'none' },
    }),
    placeholder: (provided) => ({ ...provided, color: colors.faint, margin: 0 }),
    indicatorSeparator: () => ({ display: 'none' }),
    indicatorsContainer: (provided) => ({ ...provided, paddingRight: '8px', gap: '2px' }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: colors.strong,
      opacity: 0.8,
      padding: 0,
      '&:hover': { color: colors.strong },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: colors.faint,
      padding: '2px',
      borderRadius: '6px',
      cursor: 'pointer',
      '&:hover': { color: colors.strong, backgroundColor: colors.selected },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: colors.selected,
      borderRadius: '6px',
      height: '20px',
      alignItems: 'center',
      margin: 0,
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: colors.body,
      fontSize: '12px',
      lineHeight: '16px',
      padding: '0 6px',
      paddingLeft: '6px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: colors.faint,
      borderRadius: '4px',
      paddingLeft: '2px',
      paddingRight: '4px',
      cursor: 'pointer',
      '&:hover': { backgroundColor: 'transparent', color: colors.strong },
    }),
    option: (provided, state) => ({
      ...provided,
      display: 'grid',
      gridTemplateColumns: '16px 1fr',
      alignItems: 'center',
      gap: '8px',
      minHeight: '32px',
      padding: '4px 16px 4px 8px',
      borderRadius: '6px',
      fontSize: '13px',
      lineHeight: '20px',
      cursor: 'default',
      color: colors.strong,
      backgroundColor: state.isFocused ? colors.selected : 'transparent',
      '&:active': { backgroundColor: colors.selected },
    }),
    singleValue: (provided) => ({ ...provided, color: colors.strong, margin: 0 }),
    noOptionsMessage: (provided) => ({
      ...provided,
      color: colors.faint,
      fontSize: '13px',
      padding: '8px 12px',
    }),
    loadingMessage: (provided) => ({
      ...provided,
      color: colors.faint,
      fontSize: '13px',
      padding: '8px 12px',
    }),
  };
};

const DropdownIndicator = (props: any) => (
  <selectComponents.DropdownIndicator {...props}>
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      mt="-1px"
      sx={{ '& > svg + svg': { mt: '-8px' } }}
    >
      <CaretUp size={14} weight="regular" />
      <CaretDown size={14} weight="regular" />
    </Box>
  </selectComponents.DropdownIndicator>
);

const ClearIndicator = (props: any) => (
  <selectComponents.ClearIndicator {...props}>
    <X size={14} />
  </selectComponents.ClearIndicator>
);

const MultiValueRemove = (props: any) => (
  <selectComponents.MultiValueRemove {...props}>
    <X size={12} />
  </selectComponents.MultiValueRemove>
);

const Option = (props: any) => {
  const { isSelected, children } = props;
  return (
    <selectComponents.Option {...props}>
      <Box as="span" display="flex" alignItems="center" justifyContent="center" w="16px" h="16px">
        {isSelected && <Check size={16} weight="regular" />}
      </Box>
      <Box as="span" minW={0} isTruncated>
        {children}
      </Box>
    </selectComponents.Option>
  );
};

export const selectComponentOverrides = {
  DropdownIndicator,
  ClearIndicator,
  MultiValueRemove,
  Option,
};
